import { Tag } from "../../Tag";
import { DefaultFormRenderer } from "./DefaultFormRenderer";
import type { IFormRenderer, FormWrapperOptions, FormButtonOptions, FormBoolOptions } from "./IFormRenderer";

/**
 * BareFormRenderer — rendu « headless » des items de form.
 *
 * Mode des forms convertis de mockups (MockupSectionDocument stampe
 * `renderer:"bare"` sur chaque formi) : le mockup fournit déjà tout le chrome
 * du champ (wrappers décoratifs, icônes, labels — préservés par la substitution
 * in place côté converter), ce renderer n'émet donc AUCUNE structure de
 * présentation. Contrats moteur préservés :
 *
 *  - root `div.formi-bare[formi][id][renderer=bare]` : `[formi]` = découverte
 *    par FormController.initItems ; `id` = clé des controllers maps
 *    (FormItem.getName) ; `renderer` = redécouverte du renderer en phase
 *    contrôle (FormItem.getRenderer lit l'attribut DOM). `.formi-bare` est
 *    `display:contents` (css framework pane.json) : transparent pour le layout
 *    du mockup (pilules flex, etc.).
 *  - les attrs moteur `[ft]` + `[info]` (n/name/validate/choices/max/t) vont
 *    sur le contrôle réel (descendant requis — FormItemController.createController
 *    fait un querySelector descendants-only). Le vrai <input> porte donc `name`,
 *    ce qui rend l'autofill navigateur fonctionnel (impossible avec le chrome
 *    Default où `name` vit sur un div).
 *  - pas de `.state` pré-rendu : renderState le crée à la demande dans
 *    `closest('[data-ha-state-slot]') || closest('.field') || root` pour ne
 *    jamais injecter de bloc dans le markup interne du champ.
 */
export class BareFormRenderer implements IFormRenderer {

    renderWrapper(opts: FormWrapperOptions, formItem: any): Tag {
        const infoAttrs: any[] = [
            {n: "name",    v: opts.name},
            {n: "n",       v: opts.name},
            {n: "max",     v: opts.max},
            {n: "t",       v: opts.label},
            {n: "info",    v: 1},
            {n: "choices", v: opts.choices},
        ];
        const validateStr = DefaultFormRenderer.stringifyValidate(opts.validate);
        if (validateStr && validateStr !== "{}") {
            infoAttrs.push({n: "validate", v: validateStr});
        }
        // L'id html du mockup va sur le contrôle réel : `label[for]` et les
        // sélecteurs du mockup (ex. data-ha-password-toggle="#login-pass")
        // ciblent directement l'élément labelable — le root moteur porte une
        // clé dérivée (identité FormItem.getName des controllers maps).
        if (opts.id && opts.id !== "fi" + opts.name) {
            infoAttrs.push({n: "id", v: opts.id});
        }
        formItem.attributes = [...(formItem.attributes || []), ...infoAttrs];

        return new Tag({
            attributes: BareFormRenderer.rootAttrs("fi" + opts.name),
            c: [formItem],
            classes: ["formi-bare"],
            t: "div",
        });
    }

    renderButton(opts: FormButtonOptions): Tag {
        const iconBox = opts.icon ? {t: "span", class: opts.icon} : undefined;
        return new Tag({
            attributes: BareFormRenderer.rootAttrs(),
            c: [{
                _tn: "button",
                attributes: [
                    // type=button : la soumission passe par le moteur
                    // (FormController.processBtnClick), jamais par le submit natif.
                    {n: "type",   v: "button"},
                    {n: "ft",     v: opts.ft},
                    {n: "formid", v: opts.formId},
                ],
                c: [iconBox, opts.label],
                className: opts.extraClass,
                t: "t",
            }],
            classes: ["formi-bare"],
            t: "div",
        });
    }

    renderBool(opts: FormBoolOptions): Tag {
        const attrs: any[] = [
            {n: "type", v: "checkbox"},
            {n: "ft",   v: "bool"},
            {n: "name", v: opts.name},
            {n: "n",    v: opts.name},
            {n: "info", v: 1},
        ];
        if (opts.required) { attrs.push({n: "required", v: true}); }
        if (opts.checked)  { attrs.push({n: "checked",  v: true}); }
        if (opts.id && opts.id !== "fi" + opts.name) { attrs.push({n: "id", v: opts.id}); }
        return new Tag({
            attributes: BareFormRenderer.rootAttrs("fi" + opts.name),
            c: [{_tn: "input", attributes: attrs, t: "t"}],
            classes: ["formi-bare"],
            t: "div",
        });
    }

    renderState(wrapper: Element, state: "idle" | "loading" | "error" | "success", msg?: string): void {
        if (!wrapper) { return; }
        const slot = (wrapper.closest("[data-ha-state-slot]")
            || wrapper.closest(".field")
            || wrapper) as HTMLElement;

        // Enfants directs uniquement — scan manuel : `:scope` n'est pas supporté
        // par tous les moteurs de sélecteurs (nwsapi/jsdom → SYNTAX_ERR).
        let stateEl = (Array.from(slot.children)
            .find((c) => c.classList.contains("state")) as HTMLElement | undefined) || null;
        if (!stateEl && (state === "error" || state === "success" || msg)) {
            stateEl = document.createElement("div");
            slot.appendChild(stateEl);
        }
        if (stateEl) {
            stateEl.className = state === "error"   ? "state state--error field-error"
                              : state === "success" ? "state state--success field-success"
                              : state === "loading" ? "state state--loading"
                              :                       "state state--idle";
            stateEl.textContent = msg || "";
        }

        slot.classList.toggle("is-error", state === "error");
        slot.classList.toggle("is-valid", state === "success");

        const input = wrapper.querySelector("input, textarea, select");
        if (input) {
            input.classList.toggle("is-error", state === "error");
            input.classList.toggle("is-valid", state === "success");
        }
        const button = wrapper.querySelector("button[ft]") as HTMLButtonElement | null;
        if (button) {
            button.disabled = state === "loading";
            button.classList.toggle("is-loading", state === "loading");
        }
    }

    /** Attributs du root : moteur ([formi]) + identité + renderer redécouvrable. */
    private static rootAttrs(id?: string): any[] {
        const attrs: any[] = [
            {n: "formi",    v: 1},
            {n: "renderer", v: "bare"},
        ];
        if (id) { attrs.push({n: "id", v: id}); }
        return attrs;
    }
}
